import { useQuery } from '@tanstack/react-query';
import { UserPhoneNumbersQueryKey } from '../../queryKey';
import { UsePhoneNumberByIdProps, UsePhoneNumberByIdQueryKey } from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { GenericError, UserPhoneNumber } from '@starsoft/common/models';
import { fetchPhoneNumberById } from './service';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch a specific phone number by ID
 *
 * This hook handles fetching a single phone number entry by its ID,
 * managing the query state using React Query's capabilities.
 * The data is considered fresh indefinitely (staleTime: Infinity)
 * and the query only runs when an ID is provided.
 *
 * @param {UsePhoneNumberByIdProps} props - The props object
 * @param {number} props.id - The ID of the phone number to fetch
 *
 * @returns {Object} An object containing:
 *   - error: Any error that occurred during the fetch
 *   - refetch: Function to manually trigger a refetch
 *   - isSuccess: Boolean indicating if fetch was successful
 *   - userPhoneNumber: The fetched phone number data
 *   - isPending: Boolean indicating if fetch is in progress (excluding idle state)
 */
export function usePhoneNumberById({ id }: UsePhoneNumberByIdProps) {
  const queryKey: UsePhoneNumberByIdQueryKey = [
    UserPhoneNumbersQueryKey.ById,
    id as number,
  ];
  const axiosInstance: AxiosInstance = useAxiosInstance();

  const { isPending, error, refetch, data, fetchStatus, isSuccess } = useQuery<
    UserPhoneNumber,
    AxiosError<GenericError>,
    UserPhoneNumber,
    UsePhoneNumberByIdQueryKey
  >({
    queryKey,
    queryFn: queryContext => fetchPhoneNumberById(queryContext, axiosInstance),
    staleTime: Infinity,
    enabled: !!id,
  });

  return {
    error,
    refetch,
    isSuccess,
    userPhoneNumber: data,
    isPending: isPending && fetchStatus !== 'idle',
  };
}
